import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
  IEventCalendarListRequest,
  IEventCalendarCreateRequest,
  IEventCalendarUpdateRequest,
  IEventCalendarDeleteRequest,
  IEventCalendarHideRequest,
  IEventCalendarShowRequest,
  IEventCalendarState,
  IEventCalendar,
} from "./eventCalendarReducerTypes";
import {
  initCommonClearCreate,
  initCommonClearDelete,
  initCommonClearEdit,
  initCommonClearList,
  initCommonCreate,
  initCommonCreateResult,
  initCommonDelete,
  initCommonDeleteResult,
  initCommonEdit,
  initCommonEditResult,
  initCommonGetList,
  initCommonSetListResult
} from "../highOrderReducers/commonReducer";
import {mapEventCalendarEntityToIEventCalendar} from "./eventCalendarMappers";

const eventCalendarInitialState: IEventCalendarState = {
  eventCalendarList: {
    items: [],
    isLoading: true,
    totalCount: 0,
  },
  createEventCalendar: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  updateEventCalendar: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  deleteEventCalendar: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  hideEventCalendar: {
    isLoading: false,
    isSuccess: false,
    errorMessage: '',
  },
  lastDeletedEventCalendarId: null,
  lastCreatedEventCalendarTimestamp: null,
}

const eventCalendarSlice = createSlice({
  name: 'eventCalendar',
  initialState: eventCalendarInitialState,
  reducers: {
    getEventCalendarList(state, {payload}: PayloadAction<IEventCalendarListRequest>) {
      initCommonGetList(state.eventCalendarList, {page: payload.pageModel.page})
    },
    clearEventCalendarList(state) {
      // Очищаем список событий напрямую для синхронной очистки
      state.eventCalendarList.items = [];
      state.eventCalendarList.totalCount = 0;
      state.eventCalendarList.isLoading = false;
    },
    setEventCalendarListResult(state, {payload}: PayloadAction<{items: any[], totalCount: number}>) {
      // Маппим элементы
      let totalCount = payload.totalCount;
      const mappedList = payload.items.reduce((acc: IEventCalendar[], item: any) => {
        try {
          const mappedObj = mapEventCalendarEntityToIEventCalendar(item);
          acc.push(mappedObj);
        } catch (e) {
          totalCount--;
        }
        return acc;
      }, []);
      
      // Заменяем список, а не добавляем к существующему
      state.eventCalendarList.items = mappedList;
      state.eventCalendarList.totalCount = totalCount;
      state.eventCalendarList.isLoading = false;
    },

    createEventCalendar(state, {payload}: PayloadAction<IEventCalendarCreateRequest & {requestTimestamp?: number}>) {
      initCommonCreate(state.createEventCalendar, payload);
    },
    clearCreateEventCalendar(state) {
      initCommonClearCreate(state.createEventCalendar);
      state.lastCreatedEventCalendarTimestamp = null;
    },
    setCreateEventCalendarResult(state, {payload}: PayloadAction<{item?: any, errorMessage?: string, requestTimestamp?: number}>) {
      // Явно устанавливаем isSuccess
      console.log('[eventCalendarReducer] setCreateEventCalendarResult called with payload:', payload);
      if (state.createEventCalendar) {
        if (payload && payload.item) {
          console.log('[eventCalendarReducer] Setting isSuccess = true');
          state.createEventCalendar.isSuccess = true;
          state.createEventCalendar.errorMessage = '';
          state.createEventCalendar.isLoading = false;
          // Сохраняем timestamp запроса для отслеживания последовательных созданий
          if (payload.requestTimestamp) {
            state.lastCreatedEventCalendarTimestamp = payload.requestTimestamp;
          }
        } else if (payload && payload.errorMessage) {
          console.log('[eventCalendarReducer] Setting isSuccess = false, error:', payload.errorMessage);
          state.createEventCalendar.isSuccess = false;
          state.createEventCalendar.errorMessage = payload.errorMessage;
          state.createEventCalendar.isLoading = false;
        } else {
          console.log('[eventCalendarReducer] No item or errorMessage, just setting isLoading = false');
          state.createEventCalendar.isLoading = false;
        }
        console.log('[eventCalendarReducer] Final state:', {
          isSuccess: state.createEventCalendar.isSuccess,
          isLoading: state.createEventCalendar.isLoading,
          errorMessage: state.createEventCalendar.errorMessage,
        });
      }
    },

    updateEventCalendar(state, {payload}: PayloadAction<IEventCalendarUpdateRequest>) {
      initCommonEdit(state.updateEventCalendar, payload)
    },
    clearUpdateEventCalendar(state) {
      initCommonClearEdit(state.updateEventCalendar);
    },
    setUpdateEventCalendarResult(state, {payload}: PayloadAction<{item?: any, errorMessage?: string}>) {
      if (state.eventCalendarList && payload.item) {
        const mappedItem = mapEventCalendarEntityToIEventCalendar(payload.item);
        state.eventCalendarList.items = state.eventCalendarList.items.map(item => 
          item.eventCalendarId !== mappedItem.eventCalendarId ? item : mappedItem
        );
      }
      if (state.updateEventCalendar) {
        state.updateEventCalendar.isLoading = false;
        state.updateEventCalendar.isSuccess = !!payload.item;
        state.updateEventCalendar.errorMessage = payload.errorMessage || '';
      }
    },

    deleteEventCalendar(state, {payload}: PayloadAction<IEventCalendarDeleteRequest>) {
      initCommonDelete(state.deleteEventCalendar, {itemId: payload.eventCalendarId})
    },
    clearDeleteEventCalendar(state) {
      initCommonClearDelete(state.deleteEventCalendar);
      state.lastDeletedEventCalendarId = null;
    },
    setDeleteEventCalendarResult(state, {payload}: PayloadAction<{errorMessage?: string, eventCalendarId?: number}>) {
      // Явно устанавливаем isSuccess, так как initCommonDeleteResult требует itemsObject, которого у нас нет
      if (payload.errorMessage) {
        state.deleteEventCalendar.isSuccess = false;
        state.deleteEventCalendar.errorMessage = payload.errorMessage;
      } else {
        state.deleteEventCalendar.isSuccess = true;
        state.deleteEventCalendar.errorMessage = '';
        // Сохраняем ID удаленного события для отслеживания последовательных удалений
        if (payload.eventCalendarId !== undefined) {
          state.lastDeletedEventCalendarId = payload.eventCalendarId;
        }
      }
      state.deleteEventCalendar.isLoading = false;
    },

    hideEventCalendar(state, {payload}: PayloadAction<IEventCalendarHideRequest>) {
      // Прямая установка isLoading (не используем initCommonDelete,
      // т.к. он ожидает CommonState с полем delete, а не прямой RemoteActionState)
      state.hideEventCalendar.isLoading = true;
    },
    clearHideEventCalendar(state) {
      // Прямая очистка полей RemoteActionState (не используем initCommonClearDelete,
      // т.к. он ожидает CommonState с полем delete, а не прямой RemoteActionState)
      state.hideEventCalendar.isLoading = false;
      state.hideEventCalendar.isSuccess = false;
      state.hideEventCalendar.errorMessage = '';
    },
    setHideEventCalendarResult(state, {payload}: PayloadAction<{eventCalendarId?: number, errorMessage?: string}>) {
      if (payload.errorMessage) {
        state.hideEventCalendar.isSuccess = false;
        state.hideEventCalendar.errorMessage = payload.errorMessage;
      } else {
        state.hideEventCalendar.isSuccess = true;
        state.hideEventCalendar.errorMessage = '';
        // Удаляем событие из списка только после успешного ответа от API
        if (payload.eventCalendarId !== undefined) {
          state.eventCalendarList.items = state.eventCalendarList.items.filter(
            item => item.eventCalendarId !== payload.eventCalendarId
          );
        }
      }
      state.hideEventCalendar.isLoading = false;
          },

    showEventCalendar(state, {payload}: PayloadAction<{eventCalendarId: number}>) {
      // Событие будет добавлено обратно при следующей загрузке списка
    }
  },
});

export const {
  getEventCalendarList,
  setEventCalendarListResult,
  clearEventCalendarList,

  createEventCalendar,
  setCreateEventCalendarResult,
  clearCreateEventCalendar,

  updateEventCalendar,
  setUpdateEventCalendarResult,
  clearUpdateEventCalendar,

  deleteEventCalendar,
  setDeleteEventCalendarResult,
  clearDeleteEventCalendar,

  hideEventCalendar,
  setHideEventCalendarResult,
  clearHideEventCalendar,
  showEventCalendar,
} = eventCalendarSlice.actions;

export default eventCalendarSlice.reducer;

